import React from "react";
import { Button, IconButton } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import { X } from "lucide-react";
import { HardDrive as HardDriveIcon } from "lucide-react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import useDiskUsageAlerts from "../../hooks/useDiskUsageAlerts";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === 'dark';
  return {
    alertBanner: {
      borderRadius: 10,
      margin: theme.spacing(1.5, 2, 0, 2),
      flexShrink: 0,
      boxShadow: dark ? '0 1px 4px rgba(0,0,0,0.25)' : 'none',
      border: `1px solid ${theme.palette.divider}`,
      "&.warning": {
        backgroundColor: dark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)',
        color: dark ? '#f59e0b' : '#d97706',
        borderLeft: `4px solid #f59e0b`,
      },
      "&.critical": {
        backgroundColor: dark ? 'rgba(249,115,22,0.12)' : 'rgba(249,115,22,0.08)',
        color: dark ? '#f97316' : '#ea580c',
        borderLeft: `4px solid #f97316`,
      },
      "&.full": {
        backgroundColor: dark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)',
        color: dark ? '#ef4444' : '#dc2626',
        borderLeft: `4px solid #ef4444`,
      },
    },
    alertActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    manageButton: {
      marginLeft: theme.spacing(1),
      color: 'inherit',
      borderColor: dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
    },
  };
});

const DiskUsageAlert = () => {
  const classes = useStyles();
  const history = useHistory();
  const { alert, dismissAlert } = useDiskUsageAlerts();

  if (!alert) {
    return null;
  }

  const handleManage = () => {
    history.push("/storage-management");
  };

  const getSeverity = () => {
    switch (alert.level) {
      case "full":
        return "error";
      case "critical":
        return "warning";
      case "warning":
        return "info";
      default:
        return "info";
    }
  };

  return (
    <Alert
      severity={getSeverity()}
      className={`${classes.alertBanner} ${alert.level}`}
      icon={<HardDriveIcon />}
      action={
        <div className={classes.alertActions}>
          <Button
            color="inherit"
            size="small"
            onClick={handleManage}
            className={classes.manageButton}
          >
            {i18n.t("storageManagement.manage")}
          </Button>
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={dismissAlert}
          >
            <X size={16} />
          </IconButton>
        </div>
      }
    >
      <AlertTitle>
        {alert.level === "full"
          ? i18n.t("storageManagement.alerts.full")
          : alert.level === "critical"
          ? i18n.t("storageManagement.alerts.critical", {
              percent: alert.usagePercent.toFixed(1),
            })
          : i18n.t("storageManagement.alerts.warning", {
              percent: alert.usagePercent.toFixed(1),
            })}
      </AlertTitle>
      {alert.message}
    </Alert>
  );
};

export default DiskUsageAlert;
